/**
 * Active-org context + switcher.
 *
 * `useActiveOrg` and `useMemberships` derive from the same /auth/me query,
 * so they stay in sync after `switchOrg` re-fetches it. `switchOrg` POSTs
 * to /auth/switch-org, swaps the stored token pair, and invalidates every
 * query that depends on tenant context.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ApiPaths, api } from "./api";
import { useMe } from "./auth";
import { tokenStore } from "./tokenStore";
import type { Membership, Organization, TokenPair } from "./types";

export function useActiveOrg(): Organization | null {
  const me = useMe();
  return me.data?.active_org ?? null;
}

export function useMemberships(): Membership[] {
  const me = useMe();
  return me.data?.memberships ?? [];
}

export function useSwitchOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orgId: number): Promise<TokenPair> => {
      const refresh = tokenStore.getRefresh();
      if (!refresh) throw new Error("not authenticated");
      const res = await api.post<TokenPair>(ApiPaths.authSwitchOrg, {
        org_id: orgId,
        refresh,
      });
      return res.data;
    },
    onSuccess: (pair) => {
      tokenStore.setPair(pair.access, pair.refresh);
      // Anything tenant-scoped becomes stale. Cheap to nuke; safer than a
      // selective list that drifts as new query keys are added.
      qc.invalidateQueries();
    },
  });
}